import { X, Loader2, AlertTriangle } from "lucide-react";
import { Mono, Field, Section, Toggle, inpCls, selCls } from "./ui";

export default function AssetModal({ form, setForm, meta, region, isEdit, error, saving, onClose, onSubmit }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedTreatment = meta.treatments.find((t) => t.name === form.treatment_type);
  const rate = selectedTreatment?.rates?.[region];

  const onTreatment = (name) => {
    const t = meta.treatments.find((x) => x.name === name);
    if (!t) { set("treatment_type", ""); return; }
    setForm((f) => {
      const qty = Number(f.quantity) || 0;
      const r = t.rates?.[region];
      return { ...f, treatment_type: t.name, unit_of_measure: t.unit, cet_cost: r != null && qty ? Math.round(r * qty * 100) / 100 : f.cet_cost };
    });
  };

  const onQty = (v) => {
    setForm((f) => {
      const qty = Number(v) || 0;
      const t = meta.treatments.find((x) => x.name === f.treatment_type);
      const r = t?.rates?.[region];
      return { ...f, quantity: v, cet_cost: r != null && qty ? Math.round(r * qty * 100) / 100 : f.cet_cost };
    });
  };

  const cet = Number(form.cet_cost) || 0, council = Number(form.council_cost) || 0;
  const variance = cet && form.council_cost !== "" ? ((council - cet) / cet) * 100 : null;
  const needJust = variance != null && Math.abs(variance) >= 5 && !form.cost_justification;

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="bg-navy-900 border border-gold-500/30 w-full max-w-2xl p-8 space-y-6 max-h-[92vh] overflow-y-auto" data-testid="asset-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-white">{isEdit ? "Edit asset" : "Add asset"}</h2>
          <button type="button" onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>

        {/* Identity */}
        <Section title="Identity">
          <Field label="Asset name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="asset-name" className={inpCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Asset type">
              <select value={form.asset_type} onChange={(e) => set("asset_type", e.target.value)} data-testid="asset-type" className={selCls}>
                {meta.asset_types.map((t) => <option key={t} value={t} className="bg-navy-900">{t}</option>)}
              </select>
            </Field>
            <Field label="Funding measure">
              <select value={form.funding_measure} onChange={(e) => set("funding_measure", e.target.value)} data-testid="asset-funding" className={selCls}>
                {meta.funding_measures.map((m) => <option key={m.code} value={m.code} className="bg-navy-900">{m.code} — {m.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex flex-wrap gap-6">
            <Toggle label="Emergency works" checked={form.emergency_works} onChange={(v) => set("emergency_works", v)} testid="asset-emergency" />
            <Toggle label="Restoration works" checked={form.restoration_works} onChange={(v) => set("restoration_works", v)} testid="asset-restoration" />
          </div>
        </Section>

        {/* Location */}
        <Section title="Location">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Road / location name"><input value={form.road_name} onChange={(e) => set("road_name", e.target.value)} data-testid="asset-road" className={inpCls} /></Field>
            <Field label="Road ID"><input value={form.road_id} onChange={(e) => set("road_id", e.target.value)} data-testid="asset-roadid" className={inpCls} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Carriageway"><input value={form.carriageway} onChange={(e) => set("carriageway", e.target.value)} data-testid="asset-carriageway" placeholder="e.g. LHS / both" className={inpCls} /></Field>
            <Field label="Chainage start"><input value={form.chainage_start} onChange={(e) => set("chainage_start", e.target.value)} data-testid="asset-chstart" className={inpCls} /></Field>
            <Field label="Chainage finish"><input value={form.chainage_end} onChange={(e) => set("chainage_end", e.target.value)} data-testid="asset-chend" className={inpCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Where on asset (OWP/IWP)">
              <select value={form.where_on_asset} onChange={(e) => set("where_on_asset", e.target.value)} data-testid="asset-where" className={selCls}>
                <option value="" className="bg-navy-900">—</option>
                {(meta.where_on_asset || []).map((w) => <option key={w} value={w} className="bg-navy-900">{w}</option>)}
              </select>
            </Field>
            <Field label="Site number"><input value={form.site_number} onChange={(e) => set("site_number", e.target.value)} data-testid="asset-siteno" className={inpCls} /></Field>
          </div>
        </Section>

        {/* Damage & treatment */}
        <Section title="Damage & treatment">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Damage type">
              <select value={form.damage_type} onChange={(e) => set("damage_type", e.target.value)} data-testid="asset-damagetype" className={selCls}>
                <option value="" className="bg-navy-900">—</option>
                {(meta.damage_types || []).map((t) => <option key={t} value={t} className="bg-navy-900">{t}</option>)}
              </select>
            </Field>
            <Field label={`Standard treatment (${region})`}>
              <select value={form.treatment_type} onChange={(e) => onTreatment(e.target.value)} data-testid="asset-treatment" className={selCls}>
                <option value="" className="bg-navy-900">—</option>
                {meta.treatments.map((t) => <option key={t.code} value={t.name} className="bg-navy-900">{t.name}{t.rates?.[region] != null ? ` — $${t.rates[region]}/${t.unit}` : ""}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Unit of measure"><input value={form.unit_of_measure} onChange={(e) => set("unit_of_measure", e.target.value)} data-testid="asset-uom" className={inpCls} /></Field>
            <Field label="Quantity"><input type="number" step="any" value={form.quantity} onChange={(e) => onQty(e.target.value)} data-testid="asset-quantity" className={inpCls} /></Field>
            <div><Mono className="text-white/55 text-[0.5rem] block mb-1.5">CET rate</Mono><div className="font-mono text-gold-300 text-sm py-2.5" data-testid="asset-cet-rate">{rate != null ? `$${rate}/${selectedTreatment.unit}` : "—"}</div></div>
          </div>
        </Section>

        {/* Cost */}
        <Section title="Cost — Benchmark vs Council">
          <div className="grid grid-cols-3 gap-4 items-end">
            <Field label="CET benchmark cost ($)"><input type="number" step="any" value={form.cet_cost} onChange={(e) => set("cet_cost", e.target.value)} data-testid="asset-cetcost" className={inpCls} /></Field>
            <Field label="Council cost ($)"><input type="number" step="any" value={form.council_cost} onChange={(e) => set("council_cost", e.target.value)} data-testid="asset-councilcost" className={inpCls} /></Field>
            <div><Mono className="text-white/55 text-[0.5rem] block mb-1.5">Variance</Mono><div className={`font-mono text-sm py-2.5 ${variance > 0 ? "text-red-300" : variance < 0 ? "text-emerald-300" : "text-white/60"}`} data-testid="asset-variance">{variance == null ? "—" : `${variance >= 0 ? "+" : ""}${variance.toFixed(0)}%`}</div></div>
          </div>
          <Field label="Cost justification (required when Council differs from benchmark)">
            <textarea rows={2} value={form.cost_justification} onChange={(e) => set("cost_justification", e.target.value)} data-testid="asset-justification" className={inpCls + " resize-none"} />
          </Field>
          {needJust && <div className="flex items-center gap-2 text-xs text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> Variance is {Math.abs(variance).toFixed(0)}% — a justification is strongly recommended for assessor review.</div>}
        </Section>

        <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="asset-notes" className={inpCls + " resize-none"} /></Field>

        {error && <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3" data-testid="asset-save-error">{error}</div>}

        <button type="submit" disabled={saving} data-testid="save-asset-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isEdit ? "Save changes" : "Add asset"}
        </button>
      </form>
    </div>
  );
}
